import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { RenderWithMentions } from '../Common/RenderWithMentions';
import { formatRelativeTime } from '../../firebase';
import { 
  Bell, 
  CheckCheck, 
  Trash2, 
  X, 
  AtSign, 
  MessageSquare, 
  Inbox, 
  Sparkles,
  Gift,
  Award,
  ExternalLink 
} from 'lucide-react';
import './NotificationDrawer.scss';

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    isLoading, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification 
  } = useNotifications();

  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'mentions'>('all');

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notifications.filter((n) => !n.isRead);
    }
    if (activeTab === 'mentions') {
      return notifications.filter((n) => 
        n.type.includes('mention') || 
        n.title.toLowerCase().includes('mentioned') ||
        n.snippet.includes('@')
      );
    }
    return notifications;
  }, [notifications, activeTab]);

  if (!isOpen) return null;

  const handleNotificationClick = async (notif: any) => {
    if (!notif.isRead) {
      await markAsRead(notif.id);
    }
    onClose();
    if (notif.targetUrl) {
      navigate(notif.targetUrl);
    }
  };

  const getNotificationIcon = (type: string) => {
    if (type.includes('achievement')) {
      return <Award size={14} className="text-amber-400" />;
    }
    if (type.includes('gift') || type.includes('point')) {
      return <Gift size={14} className="text-amber-400" />;
    }
    if (type.includes('mention')) {
      return <AtSign size={14} className="text-red-400" />;
    }
    if (type.includes('comment')) {
      return <MessageSquare size={14} className="text-amber-400" />;
    }
    return <Sparkles size={14} className="text-blue-400" />;
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="notification-drawer-overlay" 
        onClick={onClose} 
        aria-hidden="true" 
      />

      {/* Slide-out Tray */}
      <aside 
        className="notification-drawer-container"
        role="dialog"
        aria-modal="true"
        aria-label="Notification Center"
      >
        {/* Header */}
        <div className="notification-drawer-header">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-red-600/20 border border-red-500/30 flex items-center justify-center text-red-400">
              <Bell size={17} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-black text-white uppercase tracking-wider m-0">
                  Notification Center
                </h2>
                {unreadCount > 0 && (
                  <span className="notif-badge-pill">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <p className="text-[11px] text-white/50 m-0">Stay updated on tags & announcements</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-xs font-semibold flex items-center gap-1.5 transition-colors border border-white/5"
                title="Mark all as read"
              >
                <CheckCheck size={14} />
                <span className="hidden sm:inline text-[11px]">Read All</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
              aria-label="Close notifications"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Tab Filters */}
        <div className="notif-tabs-bar">
          <button
            onClick={() => setActiveTab('all')}
            className={`notif-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          >
            All ({notifications.length})
          </button>
          <button
            onClick={() => setActiveTab('unread')}
            className={`notif-tab-btn ${activeTab === 'unread' ? 'active' : ''}`}
          >
            Unread ({unreadCount})
          </button>
          <button
            onClick={() => setActiveTab('mentions')}
            className={`notif-tab-btn ${activeTab === 'mentions' ? 'active' : ''}`}
          >
            Mentions
          </button>
        </div>

        {/* List Content */}
        <div className="notif-list-container">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-white/40 gap-2">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-red-500 border-t-transparent"></div>
              <span className="text-xs">Loading updates...</span>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center px-6">
              <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white/30 mb-3">
                <Inbox size={26} />
              </div>
              <h3 className="text-sm font-bold text-white mb-1">All caught up!</h3>
              <p className="text-xs text-white/50 max-w-xs leading-relaxed m-0">
                {activeTab === 'unread'
                  ? 'You have zero unread notifications.'
                  : activeTab === 'mentions'
                  ? 'No @mentions found yet. When someone tags you, it will appear here.'
                  : 'You have no notifications right now.'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notif) => {
              const avatar = notif.actorAvatarUrl || '/Images/logo.png';
              const timeDisplay = notif.createdAt ? formatRelativeTime(notif.createdAt) : '';

              return (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`notif-item-card ${!notif.isRead ? 'unread' : ''}`}
                >
                  {/* Actor Avatar with Icon Badge */}
                  <div className="relative shrink-0">
                    <img
                      src={avatar}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border border-white/15 bg-white/10"
                      onError={(e) => {
                        e.currentTarget.src = '/Images/logo.png';
                      }}
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#161a23] border border-white/15 flex items-center justify-center shadow-md">
                      {getNotificationIcon(notif.type)}
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="flex-1 min-w-0 pr-1">
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <span className="text-xs font-bold text-white truncate">
                        {notif.title}
                      </span>
                      <span className="text-[10px] text-white/40 shrink-0 font-medium">
                        {timeDisplay}
                      </span>
                    </div>

                    {notif.snippet && (
                      <p className="text-xs text-white/80 leading-snug line-clamp-2 m-0 mb-1.5 break-words">
                        <RenderWithMentions text={notif.snippet} />
                      </p>
                    )}

                    <div className="flex items-center gap-1.5 text-[11px] text-red-400 font-semibold">
                      <span>View details</span>
                      <ExternalLink size={11} />
                    </div>
                  </div>

                  {/* Unread indicator or delete button */}
                  <div className="flex flex-col items-center justify-between shrink-0 gap-2">
                    {!notif.isRead && <span className="notif-unread-dot" title="Unread" />}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notif.id);
                      }}
                      className="p-1 text-white/30 hover:text-white/80 rounded transition-colors"
                      title="Dismiss"
                      aria-label="Dismiss notification"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </aside>
    </>
  );
};
